import { lazy, Suspense } from 'react'
import { BrowserRouter, Route, Switch } from 'react-router-dom'

import { WebViewPostMessageType } from '@types'
import { paths } from 'constants/paths'

import { PageLoader } from 'layouts/loaders'
import { PrivateRoute } from 'layouts/routes'

import { useWebView } from 'modules/web-view'
import { RegisterTokenContainer } from 'modules/notification'

import Bootstrap from 'pages/bootstrap'
import RedirectPage from 'pages/redirect'
import GoToPage from 'pages/public/go-to.page'

// Support
const ErrorPage = lazy(() => import('./public/error/error.page'))
const NotFoundPage = lazy(() => import('./not-found.page'))
const OnBoardingPage = lazy(() => import('./onboarding/index.page'))
const BlockedApiPage = lazy(() => import('./public/verify-access/blocked-api.page'))

// Apps
const RouterSupplier = lazy(() => import('./router.supplier'))
const RouterSalesperson = lazy(() => import('./router.salesperson'))
const MerchantPage = lazy(() => import('./merchant/index.page'))
const LoginPhoneCampaing = lazy(() => import('./merchant/login-phone-campaing/login-phone-campaing.page'))

// Auth Routes
const RouterAuth = lazy(() => import('./auth/page'))

// Public Routes
const AppStoreRedirect = lazy(() => import('./public/app-gtm/app-store-redirect.page'))
const CheckoutPage = lazy(() => import('./public/checkout/index.page'))
const CrossSellLeadPage = lazy(() => import('./public/cross-sell-lead/cross-sell-lead.page'))
const LimitReservationPage = lazy(() => import('./public/limit-reservation/index.page'))
const OptInPage = lazy(() => import('./public/opt-in/index.page'))
const OwnerBiometrics = lazy(() => import('./public/owner-biometrics/index.page'))
const PartnersPage = lazy(() => import('./public/partners/partners.page'))
const QsaPage = lazy(() => import('./public/qsa/qsa.page'))

export const Router = (props?: any) => {
  const { sendMessage } = useWebView()

  function handleUnmounted() {
    sendMessage({ type: WebViewPostMessageType.PAGE_LOAD_FINISHED, data: {} })
    // Add CSS variable to use 100vh without bugs
    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
  }

  return (
    <BrowserRouter {...props}>
      <Bootstrap location={window.location} />
      <RegisterTokenContainer />
      <Suspense fallback={<PageLoader onUnmounted={handleUnmounted} />}>
        <Switch>
          <Route exact path={paths.goTo} component={GoToPage} />
          <Route exact path={paths.error} component={ErrorPage} />
          <Route exact path={paths.blockedByNetwork} component={BlockedApiPage} />

          <Route path={paths.optIn} component={OptInPage} />
          <Route exact path={paths.crossSellLead} component={CrossSellLeadPage} />
          <Route path={paths.onboarding} component={OnBoardingPage} />
          <Route path={paths.loginPhoneCampaing} component={LoginPhoneCampaing} />
          <Route exact path={paths.qsa} component={QsaPage} />
          <Route path={paths.ownerBiometrics} component={OwnerBiometrics} />
          <Route exact path={paths.appStoreRedirect} component={AppStoreRedirect} />
          <Route exact path={paths.partners} component={PartnersPage} />

          <Route path={paths.checkout} component={CheckoutPage} />
          <Route path={paths.limitReservation} component={LimitReservationPage} />

          <Route path={paths.redirect} component={RedirectPage} />
          <PrivateRoute path={paths.supplier} component={RouterSupplier} />
          <PrivateRoute path={paths.salesperson} component={RouterSalesperson} />
          <PrivateRoute path={paths.merchant} component={MerchantPage} />

          <RouterAuth />
          <Route path="*" component={NotFoundPage} />
        </Switch>
      </Suspense>
    </BrowserRouter>
  )
}
